import { createApi } from "@reduxjs/toolkit/query/react";

import { WorkDocketStatus } from "@/constants/work-docket.constant";
import { IProject } from "@/interfaces/project.interface";
import { Docket } from "@/interfaces/workdocket.interface";
import { BaseWithAuth, getWorkspace } from "@/utils/helpers";

export interface ExtendedGeneralResponse<T> extends GeneralResponse<T> {
  lastDocketIndex: string;
}

const constructQueryString = (
  id?: string,
  filter?: string,
  status?: WorkDocketStatus,
): string => {
  let queryString = id || "";
  if (filter) {
    queryString += `?${filter}`;
  }
  if (status) {
    queryString += `&status=${status}`;
  }

  return queryString;
};

export const docketApi = createApi({
  reducerPath: "docketApi",
  baseQuery: BaseWithAuth(),
  tagTypes: ["docket", "single-docket"],
  endpoints: (builder) => ({
    getDocket: builder.query<
      ExtendedGeneralResponse<Docket[]>,
      { id?: string; filter?: string; status?: WorkDocketStatus }
    >({
      query: ({ id, filter, status }) => {
        return `/workspace/${getWorkspace()}/report/docket/${constructQueryString(id, filter, status)}`;
      },
      providesTags: ["docket"],
    }),
    getSingleDocket: builder.query<
      Docket,
      { id: string; filter?: string; status?: WorkDocketStatus }
    >({
      query: ({ id, filter, status }) => {
        return `/workspace/${getWorkspace()}/report/docket/${constructQueryString(id, filter, status)}`;
      },
      providesTags: ["single-docket"],
      transformResponse: (response: APIResponse<Docket>) => response?.data,
    }),

    getMultipleDocket: builder.query<
      { dockets: Docket[]; projects: IProject[] },
      { docketIds: string[] }
    >({
      query: ({ docketIds }) => ({
        url: `/workspace/${getWorkspace()}/report/docket/multiple?docketIds=${encodeURIComponent(
          JSON.stringify(docketIds),
        )}`,
      }),
      providesTags: ["docket"],
      transformResponse: (
        response: APIResponse<{ dockets: Docket[]; projects: IProject[] }>,
      ) => response?.data,
    }),
    mutateDocket: builder.mutation<
      object,
      { id?: string; data?: object; method: "POST" | "PUT" | "DELETE" }
    >({
      query: ({ id, data, method }) => ({
        url: `/workspace/${getWorkspace()}/report/docket/${id ? id : ""}`,
        method,
        body: data,
      }),
      // invalidatesTags: ["docket"],

      invalidatesTags: (_result, _error, { id }) => {
        const tags: ("docket" | "single-docket")[] = [];
        tags.push("docket");

        if (id) {
          tags.push("single-docket");
        }

        return tags;
      },
    }),
    mutateStatus: builder.mutation<Docket, { id: string; data: object }>({
      query: ({ id, data }) => ({
        url: `/workspace/${getWorkspace()}/report/docket/${id}/status`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["docket", "single-docket"],
    }),
    approveSignature: builder.mutation<
      object,
      { docketId: string; data: object }
    >({
      query: ({ docketId, data }) => ({
        url: `/workspace/${getWorkspace()}/report/docket/${docketId}/signature`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["docket", "single-docket"],
    }),
  }),
});

export const {
  useGetDocketQuery,
  useGetSingleDocketQuery,
  useGetMultipleDocketQuery,
  useMutateDocketMutation,
  useMutateStatusMutation,
  useApproveSignatureMutation,
  useLazyGetMultipleDocketQuery,
} = docketApi;
